import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { METRIC_KEYS } from '@/lib/analytics/metrics';

const MetricKeyEnum = z.enum(METRIC_KEYS);

/**
 * Drilldown segment: restricted to non-PII enum-shaped strings.
 * - Must start with a letter (rejects UUIDs, which start with hex digits)
 * - Allows letters, digits, underscores, spaces (max 64 chars)
 * - Explicit refine rejects UUID-shaped values as belt-and-suspenders
 */
const DrilldownSegmentSchema = z
  .string()
  .max(64)
  .regex(/^[A-Za-z][A-Za-z0-9_ ]*$/, 'invalid segment')
  .refine((v) => !/^[0-9a-f]{8}-/i.test(v), 'uuid-shaped value rejected')
  .nullable()
  .default(null);

export const FilterSchema = z.object({
  range: z.enum(['today','7d','30d','90d','qtd','ytd','custom']).default('today'),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  user: z.string().uuid().nullable().default(null),
  granularity: z.enum(['auto','hour','day','week','month']).default('auto'),
  compare: z.enum(['on', 'off']).default('on'),
  call_outcomes: z.array(z.string().max(64)).default([]),
  relationship_types: z.array(z.string().max(64)).default([]),
  drilldown: MetricKeyEnum.nullable().default(null),
  drilldown_segment: DrilldownSegmentSchema,
});
export type AnalyticsFilters = z.infer<typeof FilterSchema>;

/**
 * Synchronous safe-parse. Critical: when role is not admin and the URL contains a
 * foreign user UUID, returns filters with `user: null` IMMEDIATELY so the
 * unsafe value never reaches React Query downstream. URL cleanup is cosmetic
 * and happens in a useEffect after the initial render.
 */
export function parseFilters(
  params: URLSearchParams,
  ctx: { isAdmin: boolean; currentUserId: string },
): AnalyticsFilters {
  const raw: Record<string, unknown> = Object.fromEntries(params);
  raw.call_outcomes = params.getAll('call_outcomes');
  raw.relationship_types = params.getAll('relationship_types');
  // safeParse returns success/data; on failure (any field fails), fall back to defaults
  const parsed = FilterSchema.safeParse(raw);
  let data = parsed.success ? parsed.data : FilterSchema.parse({});
  // Synchronous safe-coerce: non-admin foreign user → null
  if (!ctx.isAdmin && data.user && data.user !== ctx.currentUserId) {
    data = { ...data, user: null };
  }
  return data;
}

interface UseAnalyticsFiltersResult {
  filters: AnalyticsFilters;
  setFilters: (patch: Partial<AnalyticsFilters>) => void;
}

export function useAnalyticsFilters(ctx: {
  isAdmin: boolean;
  currentUserId: string;
}): UseAnalyticsFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const filters = useMemo(
    () => parseFilters(searchParams, ctx),
    [searchParams, ctx.isAdmin, ctx.currentUserId],
  );

  // Cosmetic URL cleanup + toast for non-admin foreign-user-filter case.
  useEffect(() => {
    const userParam = searchParams.get('user');
    if (!ctx.isAdmin && userParam && userParam !== ctx.currentUserId) {
      const next = new URLSearchParams(searchParams);
      next.delete('user');
      setSearchParams(next, { replace: true });
      toast({ title: 'Filter not available for your role' });
    }
  }, [searchParams, ctx.isAdmin, ctx.currentUserId, setSearchParams, toast]);

  function setFilters(patch: Partial<AnalyticsFilters>) {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined) {
        next.delete(k);
      } else if (Array.isArray(v)) {
        next.delete(k);
        v.forEach((x) => next.append(k, String(x)));
      } else {
        next.set(k, String(v));
      }
    }
    setSearchParams(next, { replace: false });
  }

  return { filters, setFilters };
}
